const ISO_NUMERIC_TO_ALPHA2 = new Map(`
533:AW 004:AF 024:AO 660:AI 248:AX 008:AL 020:AD 784:AE 032:AR 051:AM 016:AS 010:AQ
260:TF 028:AG 036:AU 040:AT 031:AZ 108:BI 056:BE 204:BJ 535:BQ 854:BF 050:BD 100:BG
048:BH 044:BS 070:BA 652:BL 112:BY 084:BZ 060:BM 068:BO 076:BR 052:BB 096:BN 064:BT
074:BV 072:BW 140:CF 124:CA 166:CC 756:CH 152:CL 156:CN 384:CI 120:CM 180:CD 178:CG
184:CK 170:CO 174:KM 132:CV 188:CR 192:CU 531:CW 162:CX 136:KY 196:CY 203:CZ 276:DE
262:DJ 212:DM 208:DK 214:DO 012:DZ 218:EC 818:EG 232:ER 732:EH 724:ES 233:EE 231:ET
246:FI 242:FJ 238:FK 250:FR 234:FO 583:FM 266:GA 826:GB 268:GE 831:GG 288:GH 292:GI
324:GN 312:GP 270:GM 624:GW 226:GQ 300:GR 308:GD 304:GL 320:GT 254:GF 316:GU 328:GY
344:HK 334:HM 340:HN 191:HR 332:HT 348:HU 360:ID 833:IM 356:IN 086:IO 372:IE 364:IR
368:IQ 352:IS 376:IL 380:IT 388:JM 832:JE 400:JO 392:JP 398:KZ 404:KE 417:KG 116:KH
296:KI 659:KN 410:KR 414:KW 418:LA 422:LB 430:LR 434:LY 662:LC 438:LI 144:LK 426:LS
440:LT 442:LU 428:LV 446:MO 663:MF 504:MA 492:MC 498:MD 450:MG 462:MV 484:MX 584:MH
807:MK 466:ML 470:MT 104:MM 499:ME 496:MN 580:MP 508:MZ 478:MR 500:MS 474:MQ 480:MU
454:MW 458:MY 175:YT 516:NA 540:NC 562:NE 574:NF 566:NG 558:NI 570:NU 528:NL 578:NO
524:NP 520:NR 554:NZ 512:OM 586:PK 591:PA 612:PN 604:PE 608:PH 585:PW 598:PG 616:PL
630:PR 408:KP 620:PT 600:PY 275:PS 258:PF 634:QA 638:RE 642:RO 643:RU 646:RW 682:SA
729:SD 686:SN 702:SG 239:GS 654:SH 744:SJ 090:SB 694:SL 222:SV 674:SM 706:SO 666:PM
688:RS 728:SS 678:ST 740:SR 703:SK 705:SI 752:SE 748:SZ 534:SX 690:SC 760:SY 796:TC
148:TD 768:TG 764:TH 762:TJ 772:TK 795:TM 626:TL 776:TO 780:TT 788:TN 792:TR 798:TV
158:TW 834:TZ 800:UG 804:UA 581:UM 858:UY 840:US 860:UZ 336:VA 670:VC 862:VE 092:VG
850:VI 704:VN 548:VU 876:WF 882:WS 887:YE 710:ZA 894:ZM 716:ZW
`.trim().split(/\s+/).map((pair) => pair.split(':')));

const REGION_NAMES = new Intl.DisplayNames(['en'], { type: 'region' });

export const ISO_COUNTRIES = Object.freeze(
  [...ISO_NUMERIC_TO_ALPHA2.values()]
    .map((code) => Object.freeze({ code, name: REGION_NAMES.of(code) }))
    .sort((left, right) => left.name.localeCompare(right.name)),
);

// Static UN M49 continental groupings. They are embedded so selecting a region
// never makes a runtime network request. M49 does not list Taiwan separately;
// it is included with Asia so every ISO alpha-2 option belongs to one preset.
const CONTINENT_CODES = Object.freeze({
  africa: `
    AO BF BI BJ BW CD CF CG CI CM CV DJ DZ EG EH ER ET GA GH GM GN GQ GW IO KE KM
    LR LS LY MA MG ML MR MU MW MZ NA NE NG RE RW SC SD SH SL SN SO SS ST SZ TD TF
    TG TN TZ UG YT ZA ZM ZW
  `.trim().split(/\s+/),
  americas: `
    AG AI AR AW BB BL BM BO BQ BR BS BV BZ CA CL CO CR CU CW DM DO EC FK GD GF GL
    GP GS GT GY HN HT JM KN KY LC MF MQ MS MX NI PA PE PM PR PY SR SV SX TC TT US
    UY VC VE VG VI
  `.trim().split(/\s+/),
  antarctica: ['AQ'],
  asia: `
    AE AF AM AZ BD BH BN BT CN CY GE HK ID IL IN IQ IR JO JP KG KH KP KR KW KZ LA
    LB LK MM MN MO MV MY NP OM PH PK PS QA SA SG SY TH TJ TL TM TR TW UZ VN YE
  `.trim().split(/\s+/),
  europe: `
    AD AL AT AX BA BE BG BY CH CZ DE DK EE ES FI FO FR GB GG GI GR HR HU IE IM IS
    IT JE LI LT LU LV MC MD ME MK MT NL NO PL PT RO RS RU SE SI SJ SK SM UA VA
  `.trim().split(/\s+/),
  oceania: `
    AS AU CC CK CX FJ FM GU HM KI MH MP NC NF NR NU NZ PF PG PN PW SB TK TO TV UM
    VU WF WS
  `.trim().split(/\s+/),
});

export const COUNTRY_REGION_PRESETS = Object.freeze([
  Object.freeze({
    code: 'world',
    label: 'ui.locations.region_world',
    countries: Object.freeze(ISO_COUNTRIES.map(({ code }) => code)),
  }),
  ...Object.entries(CONTINENT_CODES).map(([code, countries]) => Object.freeze({
    code,
    label: {
      africa: 'ui.locations.region_africa',
      americas: 'ui.locations.region_americas',
      antarctica: 'ui.locations.region_antarctica',
      asia: 'ui.locations.region_asia',
      europe: 'ui.locations.region_europe',
      oceania: 'ui.locations.region_oceania',
    }[code],
    countries: Object.freeze([...countries].sort()),
  })),
]);

export function countryCodeForGeography(geography) {
  const numeric = String(geography?.id || '').padStart(3, '0');
  return ISO_NUMERIC_TO_ALPHA2.get(numeric) || null;
}

export function countryName(code) {
  return REGION_NAMES.of(String(code || '').toUpperCase());
}
